import React from 'react';
import styled from 'styled-components';
import {
  TABLE_COLUMN_MAX_WIDTH,
  TABLE_COLUMN_MIN_WIDTH,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/constants';
import { HorizontalScrollFrame } from '@/features/ai-agents/chat/MarkdownTableVisualization/HorizontalScrollFrame';
import {
  ParsedTable,
  RenderedTable,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/types';
import {
  findNumericColumnIndexes,
  formatColumnHeader,
  formatNumericCellValue,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/utils';

interface SimpleTableViewProps {
  numericColumnIndexes?: number[];
  renderedTable: RenderedTable;
  table: ParsedTable;
}

export function SimpleTableView({
  numericColumnIndexes,
  renderedTable,
  table,
}: SimpleTableViewProps) {
  const resolvedNumericColumnIndexes =
    numericColumnIndexes ?? findNumericColumnIndexes(table);

  return (
    <HorizontalScrollFrame>
      {(scrollRef) => (
        <SimpleTableWrapper ref={scrollRef}>
          <SimpleTable>
            <SimpleThead>
              <tr>
                {table.headers.map((header, columnIndex) => {
                  const isNumeric =
                    resolvedNumericColumnIndexes.includes(columnIndex);
                  const align = isNumeric ? `end` : `start`;
                  const headerContent =
                    renderedTable.headers[columnIndex] ?? header;
                  const formattedHeader =
                    typeof headerContent === `string`
                      ? formatColumnHeader(headerContent)
                      : headerContent;

                  return (
                    <SimpleTh
                      key={columnIndex}
                      $align={align}
                      $isFrozen={columnIndex === 0}
                    >
                      {formattedHeader}
                    </SimpleTh>
                  );
                })}
              </tr>
            </SimpleThead>
            <SimpleTbody>
              {table.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((value, columnIndex) => {
                    const isNumeric =
                      resolvedNumericColumnIndexes.includes(columnIndex);
                    const align = isNumeric ? `end` : `start`;
                    const cellContent =
                      renderedTable.rows[rowIndex]?.[columnIndex] ?? value;

                    return (
                      <SimpleTd
                        key={columnIndex}
                        $align={align}
                        $isFrozen={columnIndex === 0}
                      >
                        {isNumeric && typeof cellContent === `string`
                          ? formatNumericCellValue(cellContent)
                          : cellContent}
                      </SimpleTd>
                    );
                  })}
                </tr>
              ))}
            </SimpleTbody>
          </SimpleTable>
        </SimpleTableWrapper>
      )}
    </HorizontalScrollFrame>
  );
}

const SimpleTableWrapper = styled.div`
  overflow-x: auto;
  margin: 1rem 0;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border-radius: 0.5rem;
  font-family: Eesti, sans-serif;
`;

const SimpleTable = styled.table`
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  font-size: 1.4rem;
`;

const SimpleThead = styled.thead`
  background-color: rgba(0, 0, 0, 0.04);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const SimpleTbody = styled.tbody`
  tr:not(:last-child) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
`;

const SimpleTh = styled.th<{ $align?: 'start' | 'end'; $isFrozen?: boolean }>`
  position: ${({ $isFrozen }) => ($isFrozen ? `sticky` : `static`)};
  left: ${({ $isFrozen }) => ($isFrozen ? `0` : `auto`)};
  z-index: ${({ $isFrozen }) => ($isFrozen ? 3 : `auto`)};
  padding: 1.2rem 1.6rem;
  text-align: ${({ $align }) => ($align === `end` ? `right` : `left`)};
  font-weight: 600;
  color: #111;
  font-size: 1.4rem;
  min-width: ${TABLE_COLUMN_MIN_WIDTH};
  max-width: ${TABLE_COLUMN_MAX_WIDTH};
  width: ${TABLE_COLUMN_MIN_WIDTH};
  box-sizing: border-box;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
  line-height: 1.35;
  background-color: ${({ $isFrozen }) =>
    $isFrozen ? `#f4f4f5` : `transparent`};
  box-shadow: ${({ $isFrozen }) =>
    $isFrozen ? `4px 0 6px -4px rgba(0, 0, 0, 0.2)` : `none`};
`;

const SimpleTd = styled.td<{ $align?: 'start' | 'end'; $isFrozen?: boolean }>`
  position: ${({ $isFrozen }) => ($isFrozen ? `sticky` : `static`)};
  left: ${({ $isFrozen }) => ($isFrozen ? `0` : `auto`)};
  z-index: ${({ $isFrozen }) => ($isFrozen ? 2 : `auto`)};
  padding: 1.2rem 1.6rem;
  vertical-align: top;
  color: #333;
  line-height: 1.55;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
  min-width: ${TABLE_COLUMN_MIN_WIDTH};
  max-width: ${TABLE_COLUMN_MAX_WIDTH};
  width: ${TABLE_COLUMN_MIN_WIDTH};
  box-sizing: border-box;
  text-align: ${({ $align }) => ($align === `end` ? `right` : `left`)};
  background-color: ${({ $isFrozen }) =>
    $isFrozen ? `#ffffff` : `transparent`};
  box-shadow: ${({ $isFrozen }) =>
    $isFrozen ? `4px 0 6px -4px rgba(0, 0, 0, 0.12)` : `none`};
`;
